import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApiServices from '../Services/ApiServices';
import SoftGrid from '../Shared/SoftGrid/SoftGrid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHistory, faArrowRight, faClock, faCalendarDay, faCheckCircle, faPlay, faStop, faSync } from '@fortawesome/free-solid-svg-icons';
import { toastHelper } from '../../Helpers/toastHelper';

const UserSubscriptionTransaction = () => {
    const { id: subscriptionId } = useParams();
    const navigate = useNavigate();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Pagination
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const fetchTransactions = async (page = pageNumber) => {
        setLoading(true);
        try {
            const params = {
                UserSubscriptionId: subscriptionId,
                PageSize: pageSize,
                PageNumber: page,
                // IsActive: true // Based on original logic
            };

            const response = await ApiServices.get('Subscription/User/Transaction/getAll', params);

            if (response && response.check && response.data) {
                setTransactions(response.data.items || []);
                setTotalPages(response.data.total_pages || 1);
                setTotalItems(response.data.total_records || 0);
            }
        } catch (error) {
            toastHelper.error('فشل في تحميل سجل العمليات');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransactions();
    }, [pageNumber, pageSize, subscriptionId]);

    const getTransactionTypeColor = (type) => {
        switch (type) {
            case 'Renewed': return { color: '#16a34a', bg: '#dcfce7', icon: faSync };
            case 'Started': return { color: '#0284c7', bg: '#e0f2fe', icon: faPlay };
            case 'Stopped': return { color: '#dc2626', bg: '#fee2e2', icon: faStop };
            default: return { color: '#475569', bg: '#f1f5f9', icon: faClock };
        }
    };

    const columns = [
        {
            key: 'transaction_type',
            label: 'نوع العملية',
            render: (val) => {
                const styles = getTransactionTypeColor(val);
                return (
                    <div className="type-badge" style={{ backgroundColor: styles.bg, color: styles.color }}>
                        <FontAwesomeIcon icon={styles.icon} />
                        <span>{val}</span>
                    </div>
                );
            }
        },
        {
            key: 'transaction_date',
            label: 'التاريخ والوقت',
            render: (val, row) => (
                <div className="datetime-cell">
                    <div className="date">
                        <FontAwesomeIcon icon={faCalendarDay} />
                        <span>{val}</span>
                    </div>
                    <div className="time">
                        <FontAwesomeIcon icon={faClock} />
                        <span>{row.transaction_time}</span>
                    </div>
                </div>
            )
        },
        {
            key: 'notes',
            label: 'ملاحظات',
            render: (val) => val || '-'
        }
    ];

    return (
        <div className="transaction-history-page">
            <div className="page-header">
                <div className="header-left">
                    <button className="back-btn" onClick={() => navigate(-1)}>
                        <FontAwesomeIcon icon={faArrowRight} />
                        <span>عودة</span>
                    </button>
                    <div className="header-text">
                        <h1>سجل عمليات الاشتراك</h1>
                        <p>تتبع التغييرات والتجديدات الخاصة بهذا الاشتراك ({totalItems} عملية)</p>
                    </div>
                </div>
                <div className="header-icon">
                    <FontAwesomeIcon icon={faHistory} />
                </div>
            </div>

            <div className="content-card">
                <SoftGrid
                    columns={columns}
                    data={transactions}
                    isLoading={loading}
                    isPaginated={true}
                    pageNumber={pageNumber}
                    pageSize={pageSize}
                    totalPages={totalPages}
                    onPageChange={setPageNumber}
                    onPageSizeChange={setPageSize}
                />
            </div>

            <style>{`
                .transaction-history-page {
                    padding: 30px;
                    direction: rtl;
                    background: #f8fafc;
                    min-height: 100vh;
                }
                .page-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 30px;
                }
                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                }
                .back-btn {
                    padding: 10px 18px;
                    border: none;
                    background: white;
                    color: #64748b;
                    border-radius: 12px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-weight: 600;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                    transition: all 0.2s;
                }
                .back-btn:hover {
                    background: #f1f5f9;
                    color: #1e293b;
                }
                .header-text h1 {
                    font-size: 1.8rem;
                    color: #1e293b;
                    margin: 0;
                    font-weight: 800;
                }
                .header-text p {
                    color: #64748b;
                    margin: 5px 0 0;
                }
                .header-icon {
                    width: 50px;
                    height: 50px;
                    background: #eef2ff;
                    color: #6366f1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 15px;
                    font-size: 1.5rem;
                }
                .content-card {
                    background: white;
                    border-radius: 20px;
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
                    overflow: hidden;
                    padding: 20px;
                }
                .type-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 6px 14px;
                    border-radius: 10px;
                    font-weight: 700;
                    font-size: 0.85rem;
                }
                .datetime-cell {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .datetime-cell .date, .datetime-cell .time {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 0.9rem;
                    color: #475569;
                }
                .datetime-cell .date {
                    font-weight: 600;
                    color: #1e293b;
                }
                .datetime-cell .time {
                    color: #64748b;
                }
            `}</style>
        </div>
    );
};

export default UserSubscriptionTransaction;
